import { useState } from "react";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageMeta, Reveal, SectionHead } from "@/components/shared";

const EMAIL_OPTIONS = [
  {
    label: "Project & Consultancy Enquiries",
    email: "enquiries@safetysymbiosis.com",
    note: "New projects, quotations and consultancy support",
  },
];

const COUNTRIES = ["United Kingdom", "Sweden", "Portugal", "Other"];

const inputCls =
  "w-full rounded-lg border border-brand-line bg-white px-4 py-3 text-sm text-brand-charcoal placeholder:text-brand-charcoal/40 outline-none transition-colors focus:border-brand-green focus:ring-2 focus:ring-brand-green/20";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    telephone: "",
    country: "United Kingdom",
    project: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});
  const [botcheck, setBotcheck] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address";
    if (!form.message.trim()) errs.message = "Please tell us briefly about your requirement";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const accessKey = process.env.REACT_APP_WEB3FORMS_KEY;
    if (!accessKey) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: "New website enquiry",
          name: form.name,
          company: form.company,
          email: form.email,
          telephone: form.telephone,
          country: form.country,
          project: form.project,
          message: form.message,
          botcheck,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error("Request failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <PageMeta
        title="Contact SafetySymbiosis | Discuss Your HSE Requirements"
        description="Tell us about your project or workplace. Contact SafetySymbiosis to discuss HSE consultancy and safety professionals."
      />

      <section className="border-b border-brand-line bg-white py-14 lg:py-20">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">Contact</span>
            <h1 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
              Let's Talk About Your HSE Requirements
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
              Whether you need project-based HSE support, site leadership,
              contractor assurance, risk management, auditing or wider SHEQ
              support, contact SafetySymbiosis to discuss your requirements.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="wrap grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <div>
            <SectionHead eyebrow="Contact Options" title="Choose What Suits You" />
            <div className="mt-8 space-y-4">
              {EMAIL_OPTIONS.map((o) => (
                <Reveal key={o.email}>
                  <a
                    href={`mailto:${o.email}`}
                    className="card flex items-start gap-4 p-5 transition-all duration-200 hover:border-brand-green/40 hover:shadow-md"
                    data-testid={`contact-option-${o.email.split("@")[0]}`}
                  >
                    <div className="rounded-lg bg-brand-green/10 p-2.5 text-brand-green">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-heading text-sm font-bold text-brand-navy">{o.label}</p>
                      <p className="mt-0.5 text-sm font-medium text-brand-green">{o.email}</p>
                      <p className="mt-1 text-xs text-brand-charcoal/70">{o.note}</p>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.05} className="mt-8">
              <div className="card bg-brand-navy p-7" data-testid="contact-what-next-card">
                <h3 className="font-heading text-lg font-bold text-white">
                  What happens next?
                </h3>
                <ul className="mt-5 space-y-4">
                  {[
                    "Your enquiry goes straight to enquiries@safetysymbiosis.com",
                    "David or a member of the team responds personally",
                    "We agree the most practical way to support your project",
                  ].map((s, i) => (
                    <li key={i} className="flex gap-3.5 text-sm leading-relaxed text-white/75">
                      <span className="font-heading font-extrabold text-green-300">
                        {i + 1}
                      </span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div>
            <SectionHead
              eyebrow="Enquiry Form"
              title="Send Us Your Requirements"
              intro="Tell us a little about your project or workplace and we will come back to you. Enquiries are directed to enquiries@safetysymbiosis.com."
            />
            {status === "success" ? (
              <div
                className="card mt-8 flex flex-col items-start gap-4 border-brand-green/40 bg-brand-green/5 p-8"
                data-testid="contact-success-panel"
              >
                <CheckCircle2 className="h-10 w-10 text-brand-green" />
                <h3 className="font-heading text-xl font-bold">Thank you, {form.name.split(" ")[0]}.</h3>
                <p className="text-sm leading-relaxed text-brand-charcoal/85">
                  Your enquiry has been received and will be directed to
                  enquiries@safetysymbiosis.com. A member of our team will
                  respond to you shortly.
                </p>
                <button
                  onClick={() => {
                    setStatus("idle");
                    setForm({
                      name: "",
                      company: "",
                      email: "",
                      telephone: "",
                      country: "United Kingdom",
                      project: "",
                      message: "",
                    });
                  }}
                  className="btn-outline !py-2.5 text-sm"
                  data-testid="contact-success-reset-button"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="card mt-8 grid gap-5 p-8 sm:grid-cols-2" noValidate>
                <input
                  type="text"
                  name="botcheck"
                  value={botcheck}
                  onChange={(e) => setBotcheck(e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <div>
                  <label htmlFor="f-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Name *
                  </label>
                  <input id="f-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-input-name" />
                  {errors.name && <p className="mt-1 text-xs text-red-600" data-testid="contact-error-name">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="f-company" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Company
                  </label>
                  <input id="f-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" data-testid="contact-input-company" />
                </div>
                <div>
                  <label htmlFor="f-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Email *
                  </label>
                  <input id="f-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-input-email" />
                  {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="contact-error-email">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="f-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Telephone
                  </label>
                  <input id="f-phone" type="tel" className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="+44 ..." data-testid="contact-input-telephone" />
                </div>
                <div>
                  <label htmlFor="f-country" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Country / Market
                  </label>
                  <select id="f-country" className={inputCls} value={form.country} onChange={set("country")} data-testid="contact-input-country">
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="f-project" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Project / Requirement
                  </label>
                  <input id="f-project" className={inputCls} value={form.project} onChange={set("project")} placeholder="e.g. site HSE support" data-testid="contact-input-project" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="f-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-brand-navy">
                    Message *
                  </label>
                  <textarea id="f-message" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Briefly describe your project, environment and requirement..." data-testid="contact-input-message" />
                  {errors.message && <p className="mt-1 text-xs text-red-600" data-testid="contact-error-message">{errors.message}</p>}
                </div>
                {status === "error" && (
                  <p className="sm:col-span-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="contact-error-panel">
                    Something went wrong sending your enquiry. Please try again or email us directly at{" "}
                    <a href="mailto:enquiries@safetysymbiosis.com" className="font-semibold underline">
                      enquiries@safetysymbiosis.com
                    </a>
                    .
                  </p>
                )}
                <div className="sm:col-span-2">
                  <button type="submit" disabled={status === "sending"} className="btn-green w-full disabled:opacity-60 sm:w-auto" data-testid="contact-form-submit-button">
                    {status === "sending" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Enquiry"
                    )}
                  </button>
                  <p className="mt-4 text-xs leading-relaxed text-brand-charcoal/70">
                    By sending this form you agree that SafetySymbiosis may store
                    the details you provide so we can respond to your enquiry.
                    Read our{" "}
                    <Link to="/privacy" className="font-semibold text-brand-green hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
